import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticlesByCategory } from '@/mocks/articles';
import { getCategoryName } from '@/mocks/categories';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug || '');
  const category = categories.find((c) => c.slug === slug);
  const articles = getArticlesByCategory(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const sortedArticles = [...articles].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });

  if (!slug || !category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
              <h2 className="text-2xl font-bold text-foreground-700 mt-4 font-heading">分类未找到</h2>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryName} - 黑料社独家曝光 | ${category.description}`}
        description={`黑料社${categoryName}频道：${category.description}。每日更新${categoryName}最新爆料、独家内幕、深度八卦。`}
        keywords={`黑料社,${categoryName},${categoryName}爆料,${categoryName}内幕,黑料社${categoryName}`}
        canonicalUrl={`https://heiliaoshe.com/category/${slug}`}
        ogType="website"
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaoshe.com/' },
            { '@type': 'ListItem', position: 2, name: categoryName, item: `https://heiliaoshe.com/category/${slug}` },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${categoryName} - 黑料社`,
          url: `https://heiliaoshe.com/category/${slug}`,
          isPartOf: { '@type': 'WebSite', name: '黑料社' },
          description: category.description,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                  {categoryName}
                </h1>
                <p className="text-foreground-500 mt-1">{category.description}</p>
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === 'latest'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  最新
                </button>
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === 'popular'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {article.isHot && (
                      <span className="absolute top-3 left-3 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
                        热门
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-foreground-900 group-hover:text-primary-500 transition-colors font-heading line-clamp-2 text-base">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 mt-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-message-2-line"></i> {article.comments.toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {sortedArticles.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-4">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}